import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  Globe,
  FileText,
  PhoneMissed,
  Headphones,
  Layers,
  CalendarCheck,
  SearchCheck,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import PageHero from "@/components/site/PageHero";

const SWFL_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/dd575c7557e7d72528f8d94bb53752a0ac5d546bb142e6d9098a6c4d0ab73cf8.png";
const LEAD_CARD_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/d4f1e4eab0ebe102b290e0d034845b0ff38508133a3e424b175f41c5f77fb1cc.png";

const SHOWS = [
  { icon: Globe, title: "Custom HVAC website" },
  { icon: FileText, title: "Smart service-request intake" },
  { icon: PhoneMissed, title: "Missed-call follow-up concept" },
  { icon: Headphones, title: "AI receptionist concept" },
  { icon: Layers, title: "CRM-ready lead summary" },
  { icon: CalendarCheck, title: "Maintenance lead capture" },
  { icon: SearchCheck, title: "Local SEO / AEO structure" },
];

const WHY = [
  "Busy contractors miss calls during real service runs.",
  "Leads arrive scattered across phone, text, web, and email.",
  "Customers don’t always describe the problem clearly upfront.",
  "Maintenance requests get lost in the shuffle.",
];

export default function About() {
  useSeo({
    title:
      "About Sun Chaser Cooling — a PrismForge Digital demo | Southwest Florida HVAC",
    description:
      "Sun Chaser Cooling is a sample HVAC brand created by PrismForge Digital. Here's what the demo shows, why we built it, and what PrismForge actually builds for real contractors.",
  });

  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About this demo"
        breadcrumb={[{ label: "Home", to: "/" }, { label: "About" }]}
        title="About"
        highlight="Sun Chaser Cooling — a PrismForge Digital demo."
        subtitle="Sun Chaser Cooling is a sample HVAC brand created by PrismForge Digital. The site exists to demonstrate a better homeowner service-request experience — and what a modern contractor presence can actually look like."
        primaryCta={{ label: "See the demo walkthrough", to: "/demo" }}
        secondaryCta={{
          label: "Request HVAC Service",
          to: "/request-service",
        }}
        image={SWFL_IMG}
        imageAlt="Southwest Florida home at golden hour — illustrative imagery for the Sun Chaser Cooling demo brand."
        variant="light"
        testId="about-page-hero"
      />

      {/* What this demo shows */}
      <section
        data-testid="about-shows-section"
        aria-labelledby="about-shows-heading"
        className="relative bg-white py-20 sm:py-24"
      >
        <div className="container-page grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-6">
            <span className="pill">
              <Sparkles className="h-3 w-3 text-aqua" />
              What this demo shows
            </span>
            <h2
              id="about-shows-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              The full picture of a modern HVAC presence.
            </h2>
            <p className="mt-4 max-w-lg text-base text-navy/70 sm:text-lg">
              Sun Chaser Cooling isn&rsquo;t a real company &mdash; but
              everything on the site is a real, working pattern PrismForge
              Digital can implement for an actual contractor.
            </p>

            <ul className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {SHOWS.map((s) => {
                const Icon = s.icon;
                return (
                  <li
                    key={s.title}
                    className="flex items-center gap-3 rounded-xl border border-mist-line bg-white p-3 shadow-soft"
                  >
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-navy text-aqua">
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-sm font-medium text-navy/85">
                      {s.title}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="lg:col-span-6">
            <div className="relative overflow-hidden rounded-3xl border border-mist-line bg-mist shadow-lift">
              <img
                src={LEAD_CARD_IMG}
                alt="Illustration showing the clean contractor lead summary that this demo produces from every service request."
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <div
                className="absolute inset-0 bg-gradient-to-tr from-navy/30 via-transparent to-transparent"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why we built it */}
      <section
        data-testid="about-why-section"
        aria-labelledby="about-why-heading"
        className="relative bg-mist py-20 sm:py-24"
      >
        <div className="container-page max-w-4xl">
          <span className="pill">
            <span className="h-1.5 w-1.5 rounded-full bg-sun" />
            Why we built it
          </span>
          <h2
            id="about-why-heading"
            className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
          >
            Most contractor websites don&rsquo;t fix the real revenue problem.
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/75 sm:text-lg">
            A pretty homepage is the easy part. What actually moves the needle
            for a busy HVAC company is everything that happens between a
            homeowner clicking &ldquo;help&rdquo; and a technician knocking on
            the door. This demo focuses on that middle.
          </p>
          <ul className="mt-8 space-y-3">
            {WHY.map((line, i) => (
              <motion.li
                key={line}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="flex items-start gap-3 rounded-2xl border border-mist-line bg-white p-4 shadow-soft"
              >
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-aqua/15 font-display text-sm font-bold text-aqua-deep">
                  {i + 1}
                </span>
                <p className="text-[15px] text-navy/85">{line}</p>
              </motion.li>
            ))}
          </ul>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-navy/75 sm:text-lg">
            This site is a practical template for what better looks like
            &mdash; the homepage you&rsquo;d want, the intake you&rsquo;d
            actually fill out, and the lead card a dispatcher can act on
            without asking three follow-up questions.
          </p>
        </div>
      </section>

      {/* Who built this */}
      <section
        data-testid="about-prismforge-section"
        aria-labelledby="about-prismforge-heading"
        className="relative overflow-hidden bg-navy py-20 text-white sm:py-24"
      >
        <div
          className="absolute inset-0 bg-brand-gradient"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 opacity-[0.15] mix-blend-screen"
          style={{
            backgroundImage:
              "radial-gradient(circle at 12% 22%, rgba(34,211,238,0.35) 0%, transparent 45%), radial-gradient(circle at 88% 80%, rgba(245,158,11,0.18) 0%, transparent 50%)",
          }}
          aria-hidden="true"
        />
        <div className="container-page relative grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-7">
            <span className="pill-dark">
              <Sparkles className="h-3 w-3 text-sun" />
              Who built this
            </span>
            <h2
              id="about-prismforge-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl"
            >
              PrismForge Digital.
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
              PrismForge Digital builds modern websites, smart intake forms,
              call/text follow-up workflows, PrismForge Dispatch CRM setup, Make
              automations, AI receptionist/intake concepts, and booking &amp;
              lead routing systems for HVAC and home-service businesses.
            </p>
            <ul className="mt-7 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {[
                "Custom contractor websites",
                "Service request intake forms",
                "Call & text follow-up workflows",
                "PrismForge Dispatch CRM setup",
                "Make automation builds",
                "AI receptionist concepts",
                "Booking & lead routing",
              ].map((b) => (
                <li
                  key={b}
                  className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] px-3.5 py-2.5 text-sm font-medium text-white/85 backdrop-blur"
                >
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-aqua" />
                  {b}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="https://prismforgedigital.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-prismforge-link"
                className="group inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-navy shadow-soft transition-transform hover:scale-[1.02]"
              >
                Visit prismforgedigital.com
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <Link
                to="/demo"
                data-testid="about-prismforge-demo-link"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-3 text-sm font-semibold text-white transition-colors hover:border-aqua hover:text-aqua"
              >
                See the demo walkthrough
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur">
              <p className="font-display text-xl font-bold leading-tight">
                A studio for home-service businesses that want to grow without
                hiring a marketing team.
              </p>
              <p className="mt-3 text-sm text-white/75">
                Sites, intake, follow-up, CRM &mdash; designed and shipped as
                one system, not five disconnected tools.
              </p>
              <div className="mt-5 border-t border-white/10 pt-4 text-xs text-white/55">
                Sun Chaser Cooling is the demo brand. PrismForge Digital is the
                actual studio.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        data-testid="about-final-cta-section"
        aria-labelledby="about-final-cta-heading"
        className="relative bg-white py-20 sm:py-24"
      >
        <div className="container-page">
          <div className="relative overflow-hidden rounded-3xl border border-sun/30 bg-gradient-to-br from-sun/15 via-aqua/10 to-transparent p-8 sm:p-12">
            <div className="relative grid grid-cols-1 items-center gap-6 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <h2
                  id="about-final-cta-heading"
                  className="font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
                >
                  Want something like this for your HVAC company?
                </h2>
                <p className="mt-3 max-w-xl text-base text-navy/75">
                  Head over to the demo page and drop your details &mdash;
                  PrismForge will follow up about building it.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row lg:col-span-5 lg:justify-end">
                <Link
                  to="/demo"
                  data-testid="about-cta-demo-form"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white shadow-soft transition-colors hover:bg-navy-soft"
                >
                  Go to the demo form
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <a
                  href="https://prismforgedigital.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-cta-prismforge"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-sun px-6 py-3.5 text-sm font-semibold text-navy-deep shadow-sunGlow"
                >
                  Free Systems Call
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
